import {
  MerchantCodeSchema,
  PublicLatestReleaseSchema,
  ResolveResponseSchema,
  type PublicLatestRelease,
} from '@iptv/shared-types';

export const DOWNLOAD_PLATFORMS = ['android_mobile', 'android_tv', 'fire_tv'] as const;
export type DownloadPlatform = (typeof DOWNLOAD_PLATFORMS)[number];

export type ReleaseState =
  { kind: 'ok'; release: PublicLatestRelease } | { kind: 'none' } | { kind: 'error' };

export interface StoreBrand {
  code: string;
  name: string;
  logoUrl: string | null;
  primary: string;
  background: string;
  text: string;
}

type Fetch = typeof fetch;

export async function loadRelease(
  platform: DownloadPlatform,
  fetchFn: Fetch = fetch,
): Promise<ReleaseState> {
  try {
    const res = await fetchFn(`/v1/public/releases/${platform}/latest`, {
      headers: { accept: 'application/json' },
    });
    if (res.status === 404) return { kind: 'none' };
    if (!res.ok) return { kind: 'error' };
    const parsed = PublicLatestReleaseSchema.safeParse(await res.json());
    return parsed.success ? { kind: 'ok', release: parsed.data } : { kind: 'error' };
  } catch {
    return { kind: 'error' };
  }
}

/** Optional `?store=CODE` link from a merchant: brand the page with that store's name and colors. */
export async function loadStoreBrand(
  rawCode: string | null,
  fetchFn: Fetch = fetch,
): Promise<StoreBrand | null> {
  const code = MerchantCodeSchema.safeParse(rawCode?.trim().toUpperCase());
  if (!code.success) return null;
  try {
    const res = await fetchFn(
      `/v1/public/tenants/resolve/${encodeURIComponent(code.data)}?platform=android_mobile`,
      {
        headers: { accept: 'application/json' },
      },
    );
    if (!res.ok) return null;
    const parsed = ResolveResponseSchema.safeParse(await res.json());
    if (!parsed.success) return null;
    const b = parsed.data.bundle.branding;
    return {
      code: code.data,
      name: b.appName || b.storeName,
      logoUrl: b.assets.logoUrl,
      primary: b.colors.primary,
      background: b.colors.background,
      text: b.colors.text,
    };
  } catch {
    return null;
  }
}

/** Only same-origin or HTTPS downloads are offered. */
export function safeDownloadUrl(url: string | null, origin: string): string | null {
  if (!url) return null;
  try {
    const u = new URL(url, origin);
    if (u.origin === origin || u.protocol === 'https:') return u.toString();
  } catch {
    // fall through
  }
  return null;
}

export function formatBytes(bytes: number | null, locale: string): string | null {
  if (bytes === null || bytes <= 0) return null;
  const units = ['byte', 'kilobyte', 'megabyte', 'gigabyte'] as const;
  let value = bytes;
  let i = 0;
  while (value >= 1024 && i < units.length - 1) {
    value /= 1024;
    i++;
  }
  return new Intl.NumberFormat(locale, {
    style: 'unit',
    unit: units[i],
    unitDisplay: 'short',
    maximumFractionDigits: i >= 2 ? 1 : 0,
  }).format(value);
}
